import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { fcArrowDropdown } from '@seeqdev/icons/seeq';
import Button from '../Button';
import Icon from '../Icon';
import LoadingIndicator from '../LoadingIndicator';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { menuStyles, overlayStyles } from '../styles/overlays';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { IconType } from '../Icon/Icon.types';
import type { ButtonWithDropdownProps, DropdownItems } from './ButtonWithDropdown.types';

const DropdownItemIcon = ({
  item,
  xstyle,
  spinnerXstyle,
  iconType = 'text',
}: {
  item: DropdownItems;
  xstyle?: StyleXStyles;
  spinnerXstyle?: StyleXStyles;
  iconType?: IconType;
}) => {
  if (item.inProgress) {
    return (
      <LoadingIndicator
        size="sm"
        color="currentColor"
        testId={item.iconTestId}
        customId={item.iconCustomId}
        extraClassNames={item.iconExtraClassNames}
        xstyle={[spinnerXstyle, item.iconXstyle]}
      />
    );
  }
  if (!item.icon) return null;
  return (
    <Icon
      icon={item.icon}
      testId={item.iconTestId}
      type={iconType}
      color={item.iconColor}
      customId={item.iconCustomId}
      extraClassNames={item.iconExtraClassNames}
      xstyle={[xstyle, item.iconXstyle]}
    />
  );
};

/** A styled Button trigger with an accessible, portaled menu. */
const ButtonWithDropdown = (props: ButtonWithDropdownProps) => {
  const {
    label,
    variant = 'outline',
    size = 'sm',
    icon,
    iconStyle = 'text',
    iconColor,
    iconPosition = 'left',
    iconPrefix,
    extraClassNames = '',
    xstyle,
    testId,
    id,
    disabled = false,
    inProgress = false,
    'aria-label': ariaLabel,
    ...tooltipProps
  } = props;
  return (
    <Dropdown {...props}>
      <DropdownMenu.Trigger asChild id={id} disabled={disabled}>
        <Button
          label={label}
          variant={variant}
          size={size}
          icon={icon}
          iconStyle={iconStyle}
          iconColor={iconColor}
          iconPosition={iconPosition}
          iconPrefix={iconPrefix}
          extraClassNames={extraClassNames}
          xstyle={xstyle}
          testId={testId}
          disabled={disabled}
          inProgress={inProgress}
          aria-label={ariaLabel}
          tooltip={tooltipProps.tooltip}
          tooltipOptions={{ delay: tooltipProps.tooltipDelay, position: tooltipProps.tooltipPlacement }}
        />
      </DropdownMenu.Trigger>
    </Dropdown>
  );
};

export const Dropdown = ({
  dropdownItems,
  id,
  children,
  contentExtraClassNames = '',
  contentXstyle,
  containerTestId,
  align = 'end',
  placement = 'bottom',
  placementOffset = 5,
  alignOffset = -35,
  hasArrow = false,
  onOpenChange,
  isOpen,
  setFocusOnTriggerOnClose = true,
  keepFocusInsideDropdown = true,
  onContainerClick,
}: ButtonWithDropdownProps) => {
  const contentProps = stylex.props(overlayStyles.panel, overlayStyles.motion, menuStyles.content, contentXstyle);
  const subContentProps = stylex.props(overlayStyles.panel, menuStyles.content, menuStyles.subContent, contentXstyle);
  return (
    <DropdownMenu.Root defaultOpen={false} open={isOpen} onOpenChange={onOpenChange} modal={keepFocusInsideDropdown}>
      {children}
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          onClick={onContainerClick}
          sideOffset={placementOffset}
          side={placement}
          align={align}
          alignOffset={alignOffset}
          asChild
          onCloseAutoFocus={(e) => !setFocusOnTriggerOnClose && e.preventDefault()}>
          <div
            {...contentProps}
            data-testid={containerTestId}
            className={clsx(contentProps.className, contentExtraClassNames)}>
            {hasArrow && (
              <DropdownMenu.Arrow asChild>
                <div
                  {...stylex.props(overlayStyles.arrow)}
                  data-testid={containerTestId ? `${containerTestId}-arrow` : undefined}
                />
              </DropdownMenu.Arrow>
            )}
            {dropdownItems.map((item, index) => {
              const tooltipData = getQTipData(item);
              const labelProps = stylex.props(
                menuStyles.label,
                item.disabled && menuStyles.disabledText,
                item.labelXstyle,
              );
              const itemProps = stylex.props(menuStyles.item, item.disabled && menuStyles.disabled, item.itemXstyle);
              if (item.isLabel) {
                const headerProps = stylex.props(item.itemXstyle);
                return (
                  <DropdownMenu.Label
                    key={(item.id || '') + index}
                    {...headerProps}
                    className={clsx(headerProps.className, item.itemExtraClassNames)}
                    {...tooltipData}>
                    <DropdownItemIcon
                      item={item}
                      xstyle={[menuStyles.icon, menuStyles.iconColor]}
                      spinnerXstyle={menuStyles.iconColor}
                    />
                    <div
                      {...labelProps}
                      data-testid={item.labelTestId}
                      className={clsx(labelProps.className, item.labelClasses)}>
                      {item.label}
                    </div>
                  </DropdownMenu.Label>
                );
              }
              if (Array.isArray(item.subMenuItems) && item.subMenuItems.length > 0) {
                const triggerProps = stylex.props(
                  menuStyles.item,
                  menuStyles.subTrigger,
                  item.disabled && menuStyles.disabled,
                  item.itemXstyle,
                );
                const triggerLabelProps = stylex.props(
                  menuStyles.label,
                  menuStyles.subLabel,
                  item.disabled && menuStyles.dimmed,
                  item.labelXstyle,
                );
                const iconColorStyle = item.disabled ? menuStyles.disabledText : menuStyles.iconColor;
                return (
                  <DropdownMenu.Sub
                    key={(item.id || (typeof item.icon === 'string' ? item.icon : item.icon?.iconName) || '') + index}>
                    <DropdownMenu.SubTrigger
                      id={id}
                      {...triggerProps}
                      className={clsx(triggerProps.className, item.itemExtraClassNames)}
                      data-testid={item.testId}
                      disabled={item.disabled}
                      {...tooltipData}>
                      <div {...stylex.props(menuStyles.row)}>
                        {(item.icon || item.inProgress) && (
                          <div {...stylex.props(menuStyles.iconSlot)}>
                            <DropdownItemIcon
                              item={item}
                              xstyle={[menuStyles.icon, iconColorStyle]}
                              spinnerXstyle={iconColorStyle}
                            />
                          </div>
                        )}
                        <div
                          {...triggerLabelProps}
                          data-testid={item.labelTestId}
                          data-customid={item.labelCustomId}
                          className={clsx(triggerLabelProps.className, item.labelClasses)}>
                          {item.label}
                        </div>
                      </div>
                      <div {...stylex.props(menuStyles.chevronSlot)}>
                        <Icon size="sm" icon={fcArrowDropdown} xstyle={[menuStyles.chevron, iconColorStyle]} />
                      </div>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.SubContent {...stylex.props(overlayStyles.outline, overlayStyles.motion)}>
                        <div {...subContentProps} data-testid={containerTestId}>
                          {item.subMenuItems.map((subItem, subIndex) => {
                            const subLabelProps = stylex.props(
                              menuStyles.label,
                              subItem.disabled && menuStyles.disabledText,
                              item.labelXstyle,
                            );
                            return (
                              <DropdownMenu.Item
                                key={subItem.label + subIndex}
                                onSelect={subItem.onClick}
                                data-qtip-text={subItem.tooltip}
                                {...stylex.props(
                                  menuStyles.item,
                                  menuStyles.fixedHeight,
                                  subItem.disabled && menuStyles.disabled,
                                )}
                                disabled={subItem.disabled}>
                                {subItem.iconClass && (
                                  <Icon
                                    icon={subItem.iconClass}
                                    type="text"
                                    xstyle={[menuStyles.icon, item.disabled && menuStyles.dimmed]}
                                  />
                                )}
                                <div {...subLabelProps} className={clsx(subLabelProps.className, item.labelClasses)}>
                                  {subItem.label}
                                </div>
                              </DropdownMenu.Item>
                            );
                          })}
                        </div>
                      </DropdownMenu.SubContent>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Sub>
                );
              }
              return (
                <div key={(item.id || '') + index} {...tooltipData}>
                  <DropdownMenu.Item
                    data-customid={item.itemCustomId}
                    onSelect={(e) => item.onClick(e)}
                    {...itemProps}
                    className={clsx(itemProps.className, item.itemExtraClassNames)}
                    data-testid={item.testId}
                    disabled={item.disabled}>
                    {(item.icon || item.inProgress) && (
                      <div {...stylex.props(menuStyles.iconSlot)}>
                        <DropdownItemIcon
                          item={item}
                          iconType={(item.iconType || 'text') as IconType}
                          xstyle={[menuStyles.icon, item.disabled && menuStyles.dimmed]}
                          spinnerXstyle={item.disabled && menuStyles.dimmed}
                        />
                      </div>
                    )}
                    <div
                      {...labelProps}
                      data-testid={item.labelTestId}
                      data-customid={item.labelCustomId}
                      className={clsx(labelProps.className, item.labelClasses)}>
                      {item.label}
                    </div>
                  </DropdownMenu.Item>
                  {item.hasDivider && (
                    <DropdownMenu.Separator
                      data-testid={`dropdown-divider-${index}`}
                      {...stylex.props(menuStyles.separator)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default ButtonWithDropdown;
